/**
 * Pages API — response contracts.
 */

import { z } from 'zod'

import { PageSchema, PageTemplateSchema, PageVisibilitySchema } from '../../schemas/entities/page'
import { FeedItemSchema } from '../../schemas/entities/web-feed'

/** Public + authenticated read endpoints both return this shape. */
export const GetPageResponseSchema = PageSchema
export type GetPageResponse = z.infer<typeof GetPageResponseSchema>

export const PageListItemSchema = z.object({
  publicId: z.string(),
  slug: z.string(),
  title: z.string(),
  template: PageTemplateSchema,
  visibility: PageVisibilitySchema,
  hasDraft: z.boolean(),
  publishedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime()
})
export type PageListItem = z.infer<typeof PageListItemSchema>

export const ListPagesResponseSchema = z.object({
  data: z.array(PageListItemSchema)
})
export type ListPagesResponse = z.infer<typeof ListPagesResponseSchema>

export const CreatePageResponseSchema = z.object({
  publicId: z.string(),
  slug: z.string()
})
export type CreatePageResponse = z.infer<typeof CreatePageResponseSchema>

export const UpdatePageDraftResponseSchema = z.object({
  publicId: z.string(),
  // Null when the update touched only title/visibility and the page has no
  // DRAFT version (e.g. renaming a freshly-created or already-published page).
  draftPublicId: z.string().nullable()
})
export type UpdatePageDraftResponse = z.infer<typeof UpdatePageDraftResponseSchema>

export const PublishPageResponseSchema = z.object({
  publicId: z.string(),
  publishedVersionPublicId: z.string(),
  publishedAt: z.string().datetime()
})
export type PublishPageResponse = z.infer<typeof PublishPageResponseSchema>

export const DeletePageResponseSchema = z.object({
  publicId: z.string(),
  deleted: z.literal(true)
})
export type DeletePageResponse = z.infer<typeof DeletePageResponseSchema>

export const PageAssetsUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  publicUrl: z.string().url(),
  key: z.string()
})
export type PageAssetsUploadUrlResponse = z.infer<typeof PageAssetsUploadUrlResponseSchema>

/**
 * Web-feed items — the one response both the core and public variants return.
 *
 * EVERY no-content cause collapses to `data: []` with a 2xx: an unreachable
 * feed, a malformed one, a 403'd private board, a feed with zero entries, a
 * feed id matching no node, and a slug that does not resolve. That is the
 * viewer-facing failure contract — no status code, error
 * text, or retry affordance from a third party's website reaches a viewer. The
 * client cannot distinguish these cases, and deliberately so.
 *
 * `fetchedAt` is when the returned items were produced (a cache hit reports the
 * cached fetch's time, not the read's), so a client can reason about staleness
 * without the server exposing whether a fetch happened.
 */
export const GetFeedItemsResponseSchema = z.object({
  data: z.array(FeedItemSchema),
  fetchedAt: z.string().datetime()
})
export type GetFeedItemsResponse = z.infer<typeof GetFeedItemsResponseSchema>
